import { weightTrendCaption } from "../lib/recordsCopy";
import type { AnimalWeightOut } from "../types";

const VIEWBOX_WIDTH = 240;
const VIEWBOX_HEIGHT = 60;
const VERTICAL_PADDING = 8;
const SPARKLINE_WINDOW = 10;

export interface WeightSparklineProps {
  /** Newest-first, matching `useAnimalWeights`'s own state shape — this
   * component slices/reverses internally, the caller never needs to. */
  weightsNewestFirst: AnimalWeightOut[];
}

/**
 * Sprint-010 C3 (design-phase-c.md §2.5/§6/§7) — a plain inline SVG
 * polyline, no chart library, capped to the most recent 10 entries,
 * oldest -> newest left to right. Always `aria-hidden` — purely
 * decorative accelerant, exactly the weather-glyph precedent
 * (design-phase-b.md §3.2/§9). The REAL information is the adjacent
 * plain-text trend caption this component also renders (never the shape
 * alone) and the ordinary weight-row list below it.
 *
 * Renders nothing at all for 0/1 entries — "a sparkline of one point or
 * none is not a chart" (§2.5).
 */
export function WeightSparkline({ weightsNewestFirst }: WeightSparklineProps) {
  if (weightsNewestFirst.length < 2) return null;

  const window = weightsNewestFirst.slice(0, SPARKLINE_WINDOW);
  const oldestToNewest = [...window].reverse();

  const values = oldestToNewest.map((w) => w.weight_lb);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  const points = oldestToNewest.map((w, i) => {
    const x =
      oldestToNewest.length === 1
        ? VIEWBOX_WIDTH / 2
        : (i / (oldestToNewest.length - 1)) * VIEWBOX_WIDTH;
    const normalized = range === 0 ? 0.5 : (w.weight_lb - min) / range;
    // SVG y grows downward — a higher weight should sit higher (smaller y).
    const y = VERTICAL_PADDING + (1 - normalized) * (VIEWBOX_HEIGHT - VERTICAL_PADDING * 2);
    return { x, y };
  });

  const pointsAttr = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const latest = points[points.length - 1]!;
  const caption = weightTrendCaption(window);

  return (
    <>
      <div class="weight-sparkline" aria-hidden="true">
        <svg viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} preserveAspectRatio="none">
          <polyline points={pointsAttr} fill="none" stroke="var(--pine)" stroke-width="2" />
          <circle cx={latest.x} cy={latest.y} r="4" fill="var(--pine)" />
        </svg>
      </div>
      {caption ? <p class="weight-sparkline-caption">{caption}</p> : null}
    </>
  );
}
